import { Box, Chip, Stack, Typography, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { MouseEvent, ReactElement, useMemo, useRef, useState } from 'react';
import Skeleton from '../../component/skeleton/Skeleton';
import { JobPointType, UtilizationPointType } from '../../redux/slices/tools/toolsInterface';

// The plot is drawn in a 0-100 square that is stretched to fill its box, so the
// chart always spans the full width of the column it sits in. Stretching would
// distort text and strokes, so neither is drawn inside the stretched space:
// axis labels are HTML positioned around the plot, and lines use a non-scaling
// stroke.
// Percent of the plot width a month label needs to itself.
const MIN_TICK_GAP = 5;

const AXIS = { left: 34, bottom: 22, top: 10, right: 8 };
const TICKS = [0, 25, 50, 75, 100];

const x = (index: number, count: number) => (count > 1 ? (index / (count - 1)) * 100 : 0);

type Mode = 'utilization' | 'jobs';

type UtilizationChartProps = { fetching: boolean; category: string; points: UtilizationPointType[]; jobs?: JobPointType[] };

// A year of daily history for whichever cohort is selected: either the share of
// its equipment at a job site, or the number of distinct job sites it is
// deployed at. One at a time, chosen by the badges in the corner, because the
// two have unrelated scales.
//
// Drawn as inline SVG rather than pulled in from a charting library: this is the
// only chart in the app, and one line with a hover readout does not justify the
// dependency.
export const UtilizationChart = ({ fetching, category, points, jobs }: UtilizationChartProps): ReactElement => {
  const theme = useTheme();
  const plotRef = useRef<HTMLDivElement>(null);
  const [hoverIndex, setHoverIndex] = useState<number | undefined>(undefined);
  const [mode, setMode] = useState<Mode>('utilization');

  const grid = theme.palette.divider;
  const axisText = theme.palette.text.secondary;
  const showJobs = mode === 'jobs' && !!jobs?.length;
  const stroke = showJobs ? theme.palette.error.main : theme.palette.primary.main;

  // Whichever series is showing, reduced to plain values plus the top of its
  // own axis. Percentages are pinned to 100 so days are comparable across
  // categories; job counts scale to their own peak, rounded up so the four
  // gridlines land on whole jobs.
  const series = useMemo(() => {
    if (showJobs && jobs) {
      const highest = jobs.reduce((running, point) => Math.max(running, point.jobCount), 0);
      return { values: jobs.map((point) => point.jobCount), dates: jobs.map((point) => point.date), top: Math.max(4, Math.ceil(highest / 4) * 4) };
    }
    return { values: points.map((point) => point.trackedPct), dates: points.map((point) => point.date), top: 100 };
  }, [showJobs, jobs, points]);

  const geometry = useMemo(() => {
    const count = series.values.length;
    if (count < 2) {
      return undefined;
    }
    const y = (value: number) => 100 - (Math.min(Math.max(value, 0), series.top) / series.top) * 100;
    const mean = series.values.reduce((sum, value) => sum + value, 0) / count;
    const path = series.values.map((value, index) => `${index ? 'L' : 'M'}${x(index, count).toFixed(3)},${y(value).toFixed(3)}`).join(' ');

    // One tick per month, placed on the first row that falls in that month.
    const monthTicks: { index: number; label: string }[] = [];
    series.dates.forEach((date, index) => {
      const current = DateTime.fromISO(date);
      const previous = index ? DateTime.fromISO(series.dates[index - 1]) : undefined;
      if (!previous || previous.month !== current.month) {
        monthTicks.push({ index, label: current.toFormat('LLL') });
      }
    });
    // The window opens partway through a month, so that first tick is a stub:
    // it sits only a few days before the next month's and the two labels print
    // on top of each other. Drop it when it cannot clear its neighbour.
    if (monthTicks.length > 1 && x(monthTicks[1].index, count) - x(monthTicks[0].index, count) < MIN_TICK_GAP) {
      monthTicks.shift();
    }

    return { y, path, area: `${path} L100,100 L0,100 Z`, monthTicks, count, mean };
  }, [series]);

  const handleMove = (event: MouseEvent<HTMLDivElement>) => {
    const bounds = plotRef.current?.getBoundingClientRect();
    if (!geometry || !bounds || !bounds.width) {
      return;
    }
    const index = Math.round(((event.clientX - bounds.left) / bounds.width) * (geometry.count - 1));
    setHoverIndex(index >= 0 && index < geometry.count ? index : undefined);
  };

  const hoveredUtilization = hoverIndex === undefined ? undefined : points[hoverIndex];
  const hoveredJobs = hoverIndex === undefined || !jobs ? undefined : jobs[hoverIndex];
  const hoveredDate = hoverIndex === undefined ? undefined : series.dates[hoverIndex];
  const latest = series.values.length ? series.values[series.values.length - 1] : undefined;

  const formatTick = (tick: number) => (showJobs ? `${Math.round((series.top * tick) / 100)}` : `${tick}%`);

  return (
    <Box sx={{ height: '100%', minHeight: 240, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography variant="h6">
            {showJobs ? 'Job Sites' : 'Utilization'} — {category}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            {showJobs ? 'Job sites holding this equipment, past 12 months' : 'Share at job sites, past 12 months'}
            {latest !== undefined ? ` — ${showJobs ? latest.toLocaleString() : `${latest.toFixed(0)}%`} today` : ''}
          </Typography>
        </Box>

        {!!jobs?.length && (
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Chip
              label="Utilization"
              size="small"
              color={mode === 'utilization' ? 'primary' : 'default'}
              variant={mode === 'utilization' ? 'filled' : 'outlined'}
              onClick={() => setMode('utilization')}
            />
            <Chip
              label="# Jobs"
              size="small"
              color={mode === 'jobs' ? 'error' : 'default'}
              variant={mode === 'jobs' ? 'filled' : 'outlined'}
              onClick={() => setMode('jobs')}
            />
          </Stack>
        )}
      </Stack>

      {/* Everything inside is absolutely positioned so the drawing cannot feed
          its own height back into the row that sizes this column. */}
      <Box sx={{ flex: '1 1 0%', minHeight: 0, position: 'relative' }}>
        {fetching && (
          <Box sx={{ position: 'absolute', inset: 0 }}>
            <Skeleton height="100%" />
          </Box>
        )}

        {!fetching && !geometry && (
          <Typography variant="body2" color="textSecondary" sx={{ position: 'absolute', top: 0, left: 0 }}>
            Not enough history to chart.
          </Typography>
        )}

        {!fetching && geometry && (
          <>
            {TICKS.map((tick) => (
              <Typography
                key={tick}
                variant="caption"
                sx={{
                  position: 'absolute',
                  left: 0,
                  width: AXIS.left - 6,
                  textAlign: 'right',
                  color: axisText,
                  lineHeight: 1,
                  top: `calc(${AXIS.top}px + (100% - ${AXIS.top + AXIS.bottom}px) * ${(100 - tick) / 100} - 0.5em)`,
                }}
              >
                {formatTick(tick)}
              </Typography>
            ))}

            {geometry.monthTicks.map((tick) => (
              <Typography
                key={tick.index}
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  color: axisText,
                  lineHeight: 1,
                  transform: 'translateX(-50%)',
                  left: `calc(${AXIS.left}px + (100% - ${AXIS.left + AXIS.right}px) * ${x(tick.index, geometry.count) / 100})`,
                }}
              >
                {tick.label}
              </Typography>
            ))}

            <Box
              ref={plotRef}
              onMouseMove={handleMove}
              onMouseLeave={() => setHoverIndex(undefined)}
              sx={{ position: 'absolute', top: AXIS.top, bottom: AXIS.bottom, left: AXIS.left, right: AXIS.right }}
            >
              <svg
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                style={{ width: '100%', height: '100%', display: 'block', overflow: 'visible' }}
                role="img"
                aria-label={`${showJobs ? 'Job sites holding' : 'Percentage at job sites of'} ${category} equipment over the past 12 months`}
              >
                {TICKS.map((tick) => (
                  <line key={tick} x1={0} x2={100} y1={100 - tick} y2={100 - tick} stroke={grid} strokeWidth={1} vectorEffect="non-scaling-stroke" />
                ))}

                {/* The mean of the window, so a reading can be judged against
                    the period rather than only against the axis. */}
                <line
                  x1={0}
                  x2={100}
                  y1={geometry.y(geometry.mean)}
                  y2={geometry.y(geometry.mean)}
                  stroke={stroke}
                  strokeWidth={1}
                  strokeDasharray="4 4"
                  strokeOpacity={0.75}
                  vectorEffect="non-scaling-stroke"
                />

                <path d={geometry.area} fill={stroke} fillOpacity={0.12} stroke="none" />
                <path
                  d={geometry.path}
                  fill="none"
                  stroke={stroke}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />

                {hoverIndex !== undefined && (
                  <line
                    x1={x(hoverIndex, geometry.count)}
                    x2={x(hoverIndex, geometry.count)}
                    y1={0}
                    y2={100}
                    stroke={axisText}
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    vectorEffect="non-scaling-stroke"
                  />
                )}
              </svg>

              {hoverIndex !== undefined && (
                // A circle inside the stretched svg would be an ellipse.
                <Box
                  sx={{
                    position: 'absolute',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: stroke,
                    pointerEvents: 'none',
                    left: `calc(${x(hoverIndex, geometry.count)}% - 4px)`,
                    top: `calc(${geometry.y(series.values[hoverIndex])}% - 4px)`,
                  }}
                />
              )}
            </Box>

            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                right: AXIS.right,
                color: stroke,
                backgroundColor: 'background.paper',
                px: 0.5,
                lineHeight: 1,
                pointerEvents: 'none',
                top: `calc(${AXIS.top}px + (100% - ${AXIS.top + AXIS.bottom}px) * ${geometry.y(geometry.mean) / 100} - 0.5em)`,
              }}
            >
              avg {showJobs ? Math.round(geometry.mean).toLocaleString() : `${geometry.mean.toFixed(0)}%`}
            </Typography>

            {hoveredDate && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  pointerEvents: 'none',
                  backgroundColor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" fontWeight="fontWeightBold">
                  {DateTime.fromISO(hoveredDate).toLocaleString(DateTime.DATE_MED)}
                </Typography>
                {showJobs ? (
                  <Typography variant="body2">{hoveredJobs?.jobCount.toLocaleString()} job sites</Typography>
                ) : (
                  <>
                    <Typography variant="body2">{hoveredUtilization?.trackedPct.toFixed(1)}% at job sites</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {hoveredUtilization?.onsite.toLocaleString()} of {hoveredUtilization?.totalEquipment.toLocaleString()} at job sites
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
